/**
 * KPIDashboardPage - ISO 22301 Clause 9.1
 * Performance monitoring and trend analysis for BCMS
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Target,
  Activity,
  Users,
  FileCheck,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Download,
  Calendar,
  Shield,
  Zap,
  Award,
} from "lucide-react";

const KPI_CATEGORIES = [
  { id: "plan", name: "PLAN", color: "bg-blue-500", description: "Strategic Intent" },
  { id: "do", name: "DO", color: "bg-emerald-500", description: "Operational Accountability" },
  { id: "check", name: "CHECK", color: "bg-amber-500", description: "Oversight & Verification" },
  { id: "act", name: "ACT", color: "bg-purple-500", description: "Strategic Realignment" },
];

export const KPI_DEFINITIONS = [
  // PLAN KPIs
  { id: "policy_coverage", name: "Policy Coverage", category: "plan", target: 100, unit: "%", clause: "5.2", description: "Percentage of required policies documented" },
  { id: "bia_completion", name: "BIA Completion Rate", category: "plan", target: 100, unit: "%", clause: "8.2.2", description: "Percentage of critical processes with completed BIA" },
  { id: "risk_assessment_coverage", name: "Risk Assessment Coverage", category: "plan", target: 100, unit: "%", clause: "8.2.3", description: "Percentage of BIA processes with risk assessments" },
  { id: "stakeholder_engagement", name: "Stakeholder Register Completeness", category: "plan", target: 100, unit: "%", clause: "4.2", description: "Documented stakeholders with requirements" },
  
  // DO KPIs
  { id: "training_completion", name: "Training Completion Rate", category: "do", target: 90, unit: "%", clause: "7.2", description: "Staff completing required BCM training" },
  { id: "plan_currency", name: "Recovery Plan Currency", category: "do", target: 100, unit: "%", clause: "8.4", description: "Plans reviewed within last 12 months" },
  { id: "asset_inventory", name: "Asset Inventory Accuracy", category: "do", target: 95, unit: "%", clause: "8.4.4", description: "Assets with verified recovery requirements" },
  { id: "communication_test", name: "Communication Test Success", category: "do", target: 90, unit: "%", clause: "7.4", description: "Response rate in communication drills" },
  
  // CHECK KPIs
  { id: "exercise_frequency", name: "Exercise Frequency", category: "check", target: 4, unit: "/year", clause: "8.5", description: "Number of exercises conducted annually" },
  { id: "audit_findings_closed", name: "Audit Findings Closure", category: "check", target: 90, unit: "%", clause: "9.2", description: "Findings closed within target date" },
  { id: "compliance_score", name: "ISO 22301 Compliance", category: "check", target: 80, unit: "%", clause: "9.1", description: "Overall compliance score" },
  { id: "mgmt_review_frequency", name: "Management Review Frequency", category: "check", target: 2, unit: "/year", clause: "9.3", description: "Management reviews conducted annually" },
  
  // ACT KPIs
  { id: "paca_closure_rate", name: "PACA Closure Rate", category: "act", target: 90, unit: "%", clause: "10.1", description: "PACA items closed within target" },
  { id: "improvement_actions", name: "Improvement Actions Implemented", category: "act", target: 85, unit: "%", clause: "10.2", description: "Planned improvements completed" },
  { id: "lessons_learned", name: "Lessons Learned Captured", category: "act", target: 100, unit: "%", clause: "10.2", description: "Exercises with documented lessons" },
];

// Shared PDCA phase average (single source of truth for the P&C KPI tab AND the
// sidebar). Averages the measured KPIs in a category (unit-normalised, capped 100),
// returning 0 when none are measured — identical semantics everywhere.
export const computeCategoryAverage = (kpiData, category) => {
  const categoryKPIs = KPI_DEFINITIONS.filter(k => k.category === category);
  let total = 0;
  let count = 0;
  categoryKPIs.forEach(kpi => {
    const data = (kpiData || {})[kpi.id];
    // Only measured KPIs with a real value count. Unmeasured → excluded.
    if (data && data.measured === true && data.current != null) {
      const tgt = (data.target != null) ? data.target : kpi.target;
      const percentage = kpi.unit === "%" ? data.current : (data.current / tgt) * 100;
      total += Math.min(percentage, 100);
      count++;
    }
  });
  // null (not 0) when the category has no measured KPI → UI shows "Not started".
  return count > 0 ? Math.round(total / count) : null;
};

// Overall BCMS health score — SAME rule as GET /api/kpi/summary overall_score:
// mean of every MEASURED KPI's percentage (not a mean of category means).
// Returns { score:null, measured:0 } when nothing is measured → "Not started".
export const computeOverall = (kpiData) => {
  let total = 0, measured = 0;
  KPI_DEFINITIONS.forEach(kpi => {
    const d = (kpiData || {})[kpi.id];
    if (d && d.measured === true && d.current != null) {
      const tgt = (d.target != null) ? d.target : kpi.target;
      const pct = kpi.unit === "%" ? d.current : (d.current / tgt) * 100;
      total += Math.min(pct, 100);
      measured++;
    }
  });
  return { score: measured > 0 ? Math.round(total / measured) : null, measured, total: KPI_DEFINITIONS.length };
};
// defined. Powers the "1 of 4 measured" label on the P&C card and the sidebar "1/4".
export const computeCategoryCoverage = (kpiData, category) => {
  const defs = KPI_DEFINITIONS.filter(k => k.category === category);
  let measured = 0;
  defs.forEach(kpi => {
    const d = (kpiData || {})[kpi.id];
    if (d && d.measured === true && d.current != null) measured++;
  });
  return { measured, total: defs.length };
};

// Status name -> badge styles (backend now provides the status name directly).
const STATUS_STYLES = {
  on_track: { status: "on_track", color: "text-emerald-600", bg: "bg-emerald-100" },
  at_risk: { status: "at_risk", color: "text-amber-600", bg: "bg-amber-100" },
  critical: { status: "critical", color: "text-red-600", bg: "bg-red-100" },
};

export default function KPIDashboardPage({ embedded = false }) {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [kpiData, setKpiData] = useState({});
  const [enrichmentContext, setEnrichmentContext] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const fetchKPIData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get("/kpi/dashboard", {
        params: { category: selectedCategory !== "all" ? selectedCategory : undefined }
      });
      setKpiData(response.data?.kpi_data || {});
      setEnrichmentContext(response.data?.enrichment_context || null);
    } catch (error) {
      console.error("Failed to fetch KPI data:", error);
      // Fallback to empty object if API fails
      setKpiData({});
    } finally {
      setLoading(false);
    }
  }, [api, selectedCategory]);

  useEffect(() => {
    fetchKPIData();
  }, [fetchKPIData]);

  // Fallback status computation — used ONLY when the backend response omits a
  // status field for a KPI (partial-rollback safety).
  const _fallbackGetKPIStatus = (kpi, value) => {
    const percentage = kpi.unit === "%" ? value : (value / kpi.target) * 100;
    if (percentage >= 80) return STATUS_STYLES.on_track;
    if (percentage >= 50) return STATUS_STYLES.at_risk;
    return STATUS_STYLES.critical;
  };

  const getTrendIcon = (trend) => {
    if (trend === "up") return <TrendingUp className="w-4 h-4 text-emerald-500" />;
    if (trend === "down") return <TrendingDown className="w-4 h-4 text-red-500" />;
    if (trend === "stable") return <Activity className="w-4 h-4 text-slate-400" />;
    return <span className="text-slate-300 text-sm font-semibold" aria-hidden>—</span>;
  };

  const filteredKPIs = KPI_DEFINITIONS.filter(kpi => 
    selectedCategory === "all" || kpi.category === selectedCategory
  );

  // Calculate category averages (delegates to the shared helper so the sidebar
  // and this page compute identical figures).
  const getCategoryAverage = (category) => computeCategoryAverage(kpiData, category);

  // Overall BCMS health score — single source of truth (matches /api/kpi/summary).
  const overall = computeOverall(kpiData);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kpi-dashboard-page">
      {/* Header */}
      {!embedded && (
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-amber-100 text-amber-700">CHECK Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 9.1</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>KPI Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Monitor and measure BCMS performance against objectives
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current">Current Period</SelectItem>
              <SelectItem value="q1">Q1 2025</SelectItem>
              <SelectItem value="q4_2024">Q4 2024</SelectItem>
              <SelectItem value="annual">Annual</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>
      )}

      {/* Overall Health Score */}
      <Card className="bg-gradient-to-r from-sky-700 to-teal-600 text-white">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sky-100 text-sm">Overall BCMS Health Score</p>
              <p className="text-5xl font-bold mt-2" data-testid="overall-bcms-score">{overall.score === null ? "Not started" : `${overall.score}%`}</p>
              <p className="text-sky-200 text-sm mt-1">
                Based on {overall.measured} of {overall.total} measured indicators
              </p>
              {enrichmentContext && enrichmentContext.org_tier === "large_critical" && (
                <p className="text-sky-100 text-xs mt-2 inline-flex items-center gap-1 bg-white/15 rounded-full px-3 py-1" data-testid="kpi-calibration-chip">
                  <Award className="w-3 h-3" /> Targets calibrated for a large / critical-infrastructure organization
                </p>
              )}
              {enrichmentContext && enrichmentContext.org_tier === "small" && (
                <p className="text-sky-100 text-xs mt-2 inline-flex items-center gap-1 bg-white/15 rounded-full px-3 py-1" data-testid="kpi-calibration-chip">
                  <Award className="w-3 h-3" /> Targets calibrated for a small organization
                </p>
              )}
            </div>
            <div className="hidden sm:block">
              <Award className="w-24 h-24 text-white/20" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Scores */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {KPI_CATEGORIES.map((category) => {
          const score = getCategoryAverage(category.id);
          const measured = score != null;
          const cov = computeCategoryCoverage(kpiData, category.id);
          const status = !measured ? "not_started" : score >= 80 ? "on_track" : score >= 50 ? "at_risk" : "critical";
          
          return (
            <Card key={category.id} className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setSelectedCategory(category.id)}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-3">
                  <Badge className={`${category.color} text-white`}>{category.name}</Badge>
                  <Badge className={
                    status === "on_track" ? "bg-emerald-100 text-emerald-700" :
                    status === "at_risk" ? "bg-amber-100 text-amber-700" :
                    status === "not_started" ? "bg-slate-100 text-slate-500" :
                    "bg-red-100 text-red-700"
                  }>
                    {status === "on_track" ? "On Track" : status === "at_risk" ? "At Risk" : status === "not_started" ? "Not started" : "Critical"}
                  </Badge>
                </div>
                <div className="flex items-baseline gap-2">
                  <p className="text-3xl font-bold text-slate-900" data-testid={`pdca-card-score-${category.id}`}>{measured ? `${score}%` : "Not started"}</p>
                  <span className="text-xs font-medium text-slate-500" data-testid={`pdca-card-coverage-${category.id}`}>{cov.measured} of {cov.total} measured</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{category.description}</p>
                <Progress value={measured ? score : 0} className="mt-3 h-2" />
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* KPI Filter */}
      <div className="flex gap-2">
        <Button 
          variant={selectedCategory === "all" ? "default" : "outline"}
          onClick={() => setSelectedCategory("all")}
          size="sm"
        >
          All KPIs
        </Button>
        {KPI_CATEGORIES.map(cat => (
          <Button 
            key={cat.id}
            variant={selectedCategory === cat.id ? "default" : "outline"}
            onClick={() => setSelectedCategory(cat.id)}
            size="sm"
          >
            {cat.name}
          </Button>
        ))}
      </div>

      {/* KPI Table */}
      <Card>
        <CardHeader>
          <CardTitle>Performance Indicators</CardTitle>
          <CardDescription>
            Track progress against BCMS objectives mapped to ISO 22301 clauses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>KPI</TableHead>
                <TableHead>ISO Clause</TableHead>
                <TableHead>Current</TableHead>
                <TableHead>Target</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead>Trend</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredKPIs.map((kpi) => {
                const data = kpiData[kpi.id] || { current: null, previous: 0, trend: null };
                const measured = data.measured === true && data.current != null;
                // Prefer backend-provided (enrichment-scaled) target + status; fall back locally.
                const target = (data.target != null) ? data.target : kpi.target;
                const statusInfo = !measured ? null : (data.status ? (STATUS_STYLES[data.status] || _fallbackGetKPIStatus(kpi, data.current)) : _fallbackGetKPIStatus(kpi, data.current));
                const percentage = !measured ? 0 : (kpi.unit === "%" ? data.current : Math.min((data.current / target) * 100, 100));

                return (
                  <TableRow key={kpi.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-slate-900">{kpi.name}</p>
                        <p className="text-xs text-slate-500">{kpi.description}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono">{kpi.clause}</Badge>
                    </TableCell>
                    <TableCell className="font-semibold">
                      {measured ? `${data.current}${kpi.unit}` : <span className="text-slate-400 font-normal">Not measured yet</span>}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {target}{kpi.unit}
                    </TableCell>
                    <TableCell className="w-32">
                      <Progress value={percentage} className="h-2" />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {getTrendIcon(data.trend)}
                        <span className="text-xs text-slate-500">
                          {data.trend === "up" ? "Improving" : data.trend === "down" ? "Declining" : data.trend === "stable" ? "Stable" : "Not enough history"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {measured ? (
                        <Badge className={statusInfo.bg + " " + statusInfo.color}>
                          {statusInfo.status === "on_track" ? "On Track" : 
                           statusInfo.status === "at_risk" ? "At Risk" : "Critical"}
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-500">Not started</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Recommended Actions</CardTitle>
          <CardDescription>Priority actions to improve BCMS performance</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: "Complete BIA Assessments", kpi: "bia_completion", icon: FileCheck, href: "/operational-bia" },
              { title: "Schedule Training", kpi: "training_completion", icon: Users, href: "/training-awareness" },
              { title: "Conduct Exercise", kpi: "exercise_frequency", icon: Target, href: "/exercises" },
              { title: "Close PACA Items", kpi: "paca_closure_rate", icon: AlertTriangle, href: "/capa" },
              { title: "Update Recovery Plans", kpi: "plan_currency", icon: Shield, href: "/recovery-plans" },
              { title: "Management Review", kpi: "mgmt_review_frequency", icon: Calendar, href: "/management-review" },
            ].map((action, idx) => {
              const kpiDef = KPI_DEFINITIONS.find(k => k.id === action.kpi);
              const data = kpiData[action.kpi];
              const Icon = action.icon;
              
              return (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg border hover:border-sky-300 hover:bg-sky-50 transition-all cursor-pointer"
                  onClick={() => window.location.href = action.href}>
                  <div className="p-2 bg-sky-100 rounded-lg">
                    <Icon className="w-5 h-5 text-sky-700" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{action.title}</p>
                    <p className="text-xs text-slate-500">
                      Current: {data?.current || 0}{kpiDef?.unit} / Target: {(data?.target != null) ? data.target : kpiDef?.target}{kpiDef?.unit}
                    </p>
                  </div>
                  <Zap className="w-4 h-4 text-amber-500" />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
